import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";

interface LoadingTipsCarouselProps {
  messages?: string[];
  intervalMs?: number;
}

const DEFAULT_MESSAGES = [
  "Detecting biomarkers in your uploaded document…",
  "Edit any value or the measurement date before saving.",
  "Remove a biomarker anytime with the bin icon.",
  "Values in other units are auto-converted to our unit system.",
  "Each converted value shows its original detected reading below the card.",
  "Biomarkers we can't match or convert are listed separately and won't be saved.",
  "When you're ready, hit Confirm to save the results.",
];

const LoadingTipsCarousel: React.FC<LoadingTipsCarouselProps> = ({
  messages = DEFAULT_MESSAGES,
  intervalMs = 10000,
}) => {
  const theme: Theme = useTheme();
  const [index, setIndex] = useState(0);

  // Advance to the next tip on a fixed interval, looping forever.
  useEffect(() => {
    const timer = setTimeout(
      () => setIndex((prev) => (prev + 1) % messages.length),
      intervalMs
    );
    return () => clearTimeout(timer);
  }, [index, intervalMs, messages.length]);

  return (
    <div style={styles.viewport}>
      <AnimatePresence initial={false}>
        <motion.div
          key={index}
          style={styles.slide(theme)}
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: "0%", opacity: 1 }}
          exit={{ x: "-100%", opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          {messages[index]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LoadingTipsCarousel;

const styles = {
  viewport: {
    position: "relative" as "relative",
    width: "100%",
    minHeight: "40px",
    overflow: "hidden" as "hidden",
  },
  slide: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "20px",
    textAlign: "center" as "center",
  }),
};
